import { EnergyData, Resolution, TimePeriod } from "./types";
import { Instance, Site, Project, InstanceWithGenerationSummary, HealthStatus } from "../../types";
import { roundNumber } from "../../utils";
import { format, startOfISOWeek, endOfISOWeek, isSameYear, isSameMonth, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";

const isSite = (instance: Instance): instance is Site => {
    return 'devices' in instance;
}

const isProject = (instance: Instance): instance is Project => {
    return 'sites' in instance;
}

const getHealthStatusDot = (status: string) => {
    const color = getHealthStatusColor(status as HealthStatus);
    let title = status;
    switch (status) {
        case 'normal': title = 'Normal'; break;
        case 'system-fault': title = 'System fault'; break;
        case 'no-data': title = 'No data'; break;
        case 'manual': title = 'Unmonitored'; break;
        case 'under-construction': title = 'Under construction'; break;
        default: title = 'Unknown';
    }
    return <span className="mr-1" style={{ color }} title={title}>●</span>;
};

const getChartOptions = (color: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
        mode: 'index' as const,
        intersect: false,
    },
    plugins: {
        legend: {
            display: false,
        },
        title: {
            display: false,
        },
    },
    scales: {
        x: {
            ticks: {
                display: true,
                color: "#FFFFFF",
            },
            stacked: true,
        },
        y: {
            ticks: {
                display: true,
                color: "#FFFFFF",
            },
            stacked: true
        }
    },
    backgroundColor: color,
});

const formatPower = (value: number) => {
    if (!value || value <= 0) return '- kW';
    return value > 10000
        ? `${(value / 1000).toFixed(2)} MW`
        : `${value.toFixed(2)} kW`;
};

const formatEnergy = (value: number) => {
    if (!value || value <= 0) return '- kWh';
    return value > 10000
        ? `${(value / 1000).toFixed(2)} MWh`
        : `${value.toFixed(2)} kWh`;
};

const getSolarSum = (energyData: EnergyData): number => {
    if (!energyData || !energyData.results || !energyData.results["EnergyAndPowerPv"] || !energyData.results["EnergyAndPowerPv"]["pvGeneration"]) return 0;
    // Solar PV data
    const solarData = energyData.results["EnergyAndPowerPv"]["pvGeneration"];
    const res: number = roundNumber(solarData.reduce((acc, curr) => acc + (curr.value || 0), 0), 2); // kWh, 2 decimal
    return res;
}

const getBessSum = (energyData: EnergyData, type: 'charging' | 'discharging'): number => {
    if (!energyData || !energyData.results || !energyData.results["EnergyAndPowerBattery"]) return 0;
    const bessData = energyData.results["EnergyAndPowerBattery"];
    let res: number;
    if (type === 'charging') {
        const bessCharging = bessData["batteryCharging"] || [];
        if (bessCharging.length === 0) return 0;
        res = roundNumber(bessCharging.reduce((acc, curr) => acc + (curr.value || 0), 0), 2); // kWh, 2 decimal
    } else {
        const bessDischarging = bessData["batteryDischarging"] || [];
        if (bessDischarging.length === 0) return 0;
        res = roundNumber(bessDischarging.reduce((acc, curr) => acc + (curr.value || 0), 0), 2); // kWh, 2 decimal
    }
    return res;
}

const makeChartData = (energyData: EnergyData): { bessChargingData: number[], bessDischargingData: number[], solarData: number[], consumptionData: number[] } => {
    const results = energyData.results;

    let bessChargingData: number[] = [], bessDischargingData: number[] = [], solarData: number[] = [], consumptionData: number[] = [];

    if (results["EnergyAndPowerBattery"]?.["batteryCharging"]) {
        bessChargingData = results["EnergyAndPowerBattery"]?.["batteryCharging"]?.map((item) => -(item.value || 0)) || []; // Convert to kWh
    }
    if (results["EnergyAndPowerBattery"]?.["batteryDischarging"]) {
        bessDischargingData = results["EnergyAndPowerBattery"]?.["batteryDischarging"]?.map((item) => (item.value || 0)) || []; // Convert to kWh
    }
    if (results["EnergyAndPowerPv"]?.["pvGeneration"]) {
        solarData = results["EnergyAndPowerPv"]?.["pvGeneration"]?.map((item) => (item.value || 0)) || []; // Convert to kWh
    }

    if (results["EnergyAndPowerPv"]?.["pvGeneration"] && results["EnergyAndPowerBattery"]?.["batteryCharging"] && results["EnergyAndPowerBattery"]?.["batteryDischarging"] && bessChargingData.length === bessDischargingData.length && bessChargingData.length === solarData.length) {
        consumptionData = solarData.map((item, index) => -Math.abs(
            Math.abs(item) - Math.abs(bessChargingData[index]) + Math.abs(bessDischargingData[index])
        ));
    }

    return { bessChargingData, bessDischargingData, solarData, consumptionData };
}

const getHealthStatusColor = (healthStatus: HealthStatus): string => {
    switch (healthStatus) {
        case 'normal':
            return '#36D399';
        case 'system-fault':
            return '#F87272';
        case 'no-data':
            return '#D1D5DB';
        case 'manual':
            return '#93C5FD';
        case 'under-construction':
            return '#827b7b';
        default:
            return '#9CA3AF';
    }
}

const getAvailableResolutions = (period: TimePeriod): Resolution[] => {
    switch (period) {
        case TimePeriod.Day:
            return [Resolution.Mins, Resolution.Hour];
        case TimePeriod.Week:
            return [Resolution.Hour, Resolution.Day];
        case TimePeriod.Month:
            return [Resolution.Day, Resolution.Week];
        case TimePeriod.Year:
            return [Resolution.Week, Resolution.Month];
    }
}

const dateToDateOrDateRange = (date: Date, period: TimePeriod): Date | { from: Date, to: Date } => {
    switch (period) {
        case TimePeriod.Day:
            return date;
        case TimePeriod.Week:
            return { from: startOfISOWeek(date), to: endOfISOWeek(date) };
        case TimePeriod.Month:
            return { from: startOfMonth(date), to: endOfMonth(date) };
        case TimePeriod.Year:
            return { from: startOfYear(date), to: endOfYear(date) };
    }
}

const dateToDisplay = (date: Date, period: TimePeriod): string => {
    switch (period) {
        case TimePeriod.Day:
            return format(date, 'iii, d MMM, yyyy'); // Thu, 4 Sep., 2025
        case TimePeriod.Week:
            const startDate = startOfISOWeek(date);
            const endDate = endOfISOWeek(date);
            if (isSameYear(startDate, endDate)) {
                if (isSameMonth(startDate, endDate)) {
                    // 1 - 7 Sep., 2025
                    return `${format(startDate, 'd')} - ${format(endDate, 'd')} ${format(startDate, 'MMM, yyyy')}`;
                } else {
                    // 29 Sep. - 5 Oct., 2025
                    return `${format(startDate, 'd MMM')} - ${format(endDate, 'd MMM')}, ${format(startDate, 'yyyy')}`;
                }
            } else {
                // 29 Dec. 2025 - 4 Jan., 2026
                return `${format(startDate, 'd MMM, yyyy')} - ${format(endDate, 'd MMM, yyyy')}`;
            }
        case TimePeriod.Month:
            return format(date, 'MMM, yyyy'); // Sep., 2025
        case TimePeriod.Year:
            return format(date, 'yyyy'); // 2025
    }
}

const getConsumption = (instanceWithGenerationSummary: InstanceWithGenerationSummary): number => {
    // Consumption = Pc + Bc = pvGeneration - batteryCharging + batteryDischarging
    if (!instanceWithGenerationSummary || !instanceWithGenerationSummary.results || !instanceWithGenerationSummary.results["EnergyAndPowerPv"] || !instanceWithGenerationSummary.results["EnergyAndPowerBattery"]) return 0;
    const bessData = instanceWithGenerationSummary.results["EnergyAndPowerBattery"];
    const pvData = instanceWithGenerationSummary.results["EnergyAndPowerPv"];
    const consumption = (pvData["pvGeneration"] || 0) - (bessData["batteryCharging"] || 0) + (bessData["batteryDischarging"] || 0);
    
    return consumption;
}

const getConsumptionSum = (energyData: EnergyData): number => {
    if (!energyData?.results) return 0;

    const pvGeneration = energyData.results["EnergyAndPowerPv"]?.["pvGeneration"] || [];
    const batteryCharging = energyData.results["EnergyAndPowerBattery"]?.["batteryCharging"] || [];
    const batteryDischarging = energyData.results["EnergyAndPowerBattery"]?.["batteryDischarging"] || [];

    const solarTotal = pvGeneration.reduce((acc, curr) => acc + (curr.value || 0), 0);
    const chargingTotal = batteryCharging.reduce((acc, curr) => acc + (curr.value || 0), 0);
    const dischargingTotal = batteryDischarging.reduce((acc, curr) => acc + (curr.value || 0), 0);

    return roundNumber(solarTotal - chargingTotal + dischargingTotal, 2);
}

export { isSite, isProject, getChartOptions, getHealthStatusDot, getSolarSum, getBessSum, makeChartData, getHealthStatusColor, getAvailableResolutions, dateToDisplay, dateToDateOrDateRange, formatEnergy, getConsumption, getConsumptionSum, formatPower };
